import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { Highlight } from '@tiptap/extension-highlight';
import { Editor, EditorContent, Extension, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Paragraph from '@tiptap/extension-paragraph';
import { useEffect, useState } from 'react';
import { SwatchesPicker } from '../colorPicker/ColorPicker';
import "./TextEditor.scss";
import {
    List,
    ListOrdered,
    Redo, Undo, Link as Link2,
    Bold, Italic, Strikethrough,
    Underline as UnderlineIcon,
    Baseline,
    Highlighter,
    Trash2,
    Link2Off,
    Undo2,
    ExternalLink,
    Save
} from 'lucide-react';

export const CustomTab = Extension.create({
    name: 'customTab',

    addKeyboardShortcuts() {
        return {
            Tab: () => {
                const { editor } = this;
                editor.commands.insertContent('  ');
                return true;
            },
        }
    },
})

export const extensions = [
    CustomTab,
    Color.configure({
        types: [TextStyle.name],
    }),
    Highlight.configure({
        multicolor: true,
    }),
    TextStyle,
    Paragraph,
    Underline,
    StarterKit.configure({
        bulletList: {
            keepMarks: true,
            keepAttributes: false,
        },
        orderedList: {
            keepMarks: true,
            keepAttributes: false,
        },
    }),
    Link.extend({
        inclusive: false
    }).configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
        protocols: ['http', 'https'],
        isAllowedUri: (url, ctx) => {
            try {
                const parsedUrl = url.includes(':') ? new URL(url) : new URL(`${ctx.defaultProtocol}://${url}`)
                if (!ctx.defaultValidate(parsedUrl.href)) {
                    return false
                }
                const disallowedProtocols = ['ftp', 'file', 'mailto']
                const protocol = parsedUrl.protocol.replace(':', '')
                if (disallowedProtocols.includes(protocol)) {
                    return false
                }
                const allowedProtocols = ctx.protocols.map(p => (typeof p === 'string' ? p : p.scheme))

                if (!allowedProtocols.includes(protocol)) {
                    return false
                }
                return true
            } catch {
                return false
            }
        },
        shouldAutoLink: url => {
            try {
                void (url.includes(':') ? new URL(url) : new URL(`https://${url}`))
                return true;
            } catch {
                return false
            }
        },
    })
]

type Props = {
    preset?: string | null,
    ref?: (instance: { getEditor: () => Editor | null }) => void
}

export const TextEditor = ({ preset, ref }: Props) => {
    const [linkInput, setLinkInput] = useState(false);
    const [url, setUrl] = useState("");

    const [textColor, setTextColor] = useState("#000000");
    const [textBg, setTextBg] = useState("#ffffff");

    const [selectTextColor, setSelectTextColor] = useState(false);
    const [selectBgColor, setSelectBgTextColor] = useState(false);

    const editor = useEditor({
        editable: true,
        extensions,
        editorProps: {
            handleDOMEvents: {
                click: (_view, event) => {
                    const target = event.target as HTMLElement;
                    const link = target.closest('a') as HTMLAnchorElement | null;

                    if (link && link.getAttribute('href')) {
                        const linkText = link.textContent?.trim() || "";

                        try {
                            new URL(linkText);
                            event.preventDefault();
                            window.open(linkText, "_blank");
                            return true;
                        } catch {
                            setLinkInput(true);
                            setUrl(link.href || "");
                            return true;
                        }
                    }
                    setUrl("");
                    setLinkInput(false);
                    return false;
                },
            },
        },
    });
    useEffect(() => {
        if (editor && typeof ref === "function") {
            ref({
                getEditor: () => editor,
            });
        }
    }, [editor, ref]);

    useEffect(() => {
        if (editor) editor.chain().focus().setColor(textColor).run();
    }, [textColor]);

    useEffect(() => {
        if (editor) editor.chain().focus().toggleHighlight({ color: textBg }).run();
    }, [textBg]);

    useEffect(() => {
        if (editor && preset) editor.commands.insertContent(preset);
    }, [])
    if (!editor) return null;

    const setLink = () => {
        if (url === null) return;
        if (url.length === 0) {
            editor.chain()
                .focus()
                .extendMarkRange('link')
                .unsetLink()
                .run()
            return setLinkInput(false)
        };
        if (url === '') {
            return editor.chain()
                .focus()
                .extendMarkRange('link')
                .unsetLink()
                .run()
        };

        editor.chain()
            .focus()
            .focus()
            .extendMarkRange('link')
            .unsetMark('textStyle')
            .setLink({ href: url })
            .run();
        setLinkInput(false);
    }

    return (
        <div className="editor-container">
            {linkInput ? <div className="hyperlink">
                <div className="hyperlink__input-container">
                    <input type="url"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        autoFocus={true} />
                </div>
                <button onClick={setLink} type="button">
                    <Save width={18} height={18} />
                </button>
                {editor.getAttributes('link').href && <>
                    <button type="button" onClick={() => window.open(url, "_blank")}>
                        <ExternalLink width={18} height={18} />
                    </button>
                    <button type="button" onClick={() => {
                        editor.chain().focus().unsetLink().run();
                        setUrl("");
                        setLinkInput(false);
                    }}>
                        <Link2Off width={18} height={18} />
                    </button>
                </>}
                <button onClick={() => setLinkInput(false)}>
                    <Undo2 width={18} height={18} />
                </button>
            </div> :
                <div className="toolbar">
                    <div className="toolbar__section">
                        <button
                            onClick={() => editor.chain().focus().undo().run()}
                            disabled={!editor.can().undo()}
                            type="button">
                            <Undo width={18} height={18} />
                        </button>
                        <button
                            onClick={() => editor.chain().focus().redo().run()}
                            disabled={!editor.can().redo()}
                            type="button">
                            <Redo width={18} height={18} />
                        </button>
                    </div>
                    <div className="toolbar__section">
                        <button
                            onClick={() => editor.chain().focus().toggleBulletList().run()}
                            type="button">
                            <List width={18} height={18} />
                        </button>
                        <button
                            onClick={() => editor.chain().focus().toggleOrderedList().run()}
                            type="button">
                            <ListOrdered width={18} height={18} />
                        </button>
                    </div>
                    <div className="toolbar__section">
                        <button
                            onClick={() => setLinkInput(true)}
                            disabled={editor.state.selection.empty}
                            type="button">
                            <Link2 width={18} height={18} />
                        </button>
                        {/*   <button
                            onClick={() => editor.chain().focus().unsetLink().run()}
                            disabled={!editor.isActive('link')}
                            type="button"
                        >
                            Unset link
                        </button> */}
                    </div>
                    <div className="toolbar__section">
                        <button onClick={() => editor.chain().focus().toggleBold().run()}
                            className={`${editor.isActive('bold') ? "toolbar__active-button" : ""}`}
                            type="button">
                            <Bold width={18} height={18} />
                        </button>
                        <button onClick={() => editor.chain().focus().toggleItalic().run()}
                            className={`${editor.isActive("italic") ? "toolbar__active-button" : ""}`}
                            type="button">
                            <Italic width={18} height={18} />
                        </button>
                        <button onClick={() => editor.chain().focus().toggleStrike().run()}
                            className={`${editor.isActive('strike') ? "toolbar__active-button" : ""}`}
                            type="button">
                            <Strikethrough width={18} height={18} />
                        </button>
                        <button onClick={() => editor.chain().focus().toggleUnderline().run()}
                            className={`${editor.isActive('underline') ? "toolbar__active-button" : ""}`}
                            type="button">
                            <UnderlineIcon width={18} height={18} />
                        </button>
                    </div>
                    <div className="toolbar__section">
                        <button
                            className={`toolbar__color-button ${selectTextColor ? "toolbar__active-button" : ""}`}
                            onClick={() => {
                                setSelectTextColor(true);
                                setSelectBgTextColor(false);
                            }}
                            type="button">
                            <Baseline width={18} height={18} />
                            <div className="toolbar__current-color" style={{ backgroundColor: `${textColor}` }} />
                        </button>
                        <button
                            className={`toolbar__color-button ${selectBgColor ? "toolbar__active-button" : ""}`}
                            onClick={() => {
                                setSelectBgTextColor(true);
                                setSelectTextColor(false);
                            }}
                            type="button">
                            <Highlighter width={18} height={18} />
                            <div className="toolbar__current-color" style={{ backgroundColor: `${textBg}` }} />
                        </button>
                        {selectBgColor && <div className="toolbar__color-picker-container">
                            <SwatchesPicker
                                color={textBg}
                                onChange={setTextBg}
                                onClose={() => {
                                    setSelectBgTextColor(false);
                                    editor.commands.focus();
                                }}
                                setDefault={() => setTextBg("#ffffff")}
                                textColor={textColor}
                                bgColor={textBg}
                            />
                        </div>
                        }
                        {selectTextColor && <div className="toolbar__color-picker-container">
                            <SwatchesPicker
                                color={textColor}
                                onChange={setTextColor}
                                onClose={() => {
                                    setSelectTextColor(false);
                                    editor.commands.focus();
                                }}
                                setDefault={() => setTextColor("#000000")}
                                textColor={textColor}
                                bgColor={textBg} />
                        </div>
                        }
                    </div>
                </div>}
            <div className="editor-wrapper">
                <EditorContent editor={editor} tabIndex={-1} />
            </div>
            <button
                className="editor-clear"
                onClick={() => editor.commands.clearContent()}
                type="button">
                <Trash2 width={18} height={18} />
            </button>
        </div>
    )
}